import { type ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";

import { backTargetFromLocation, withBackTarget } from "@/app/back-target";
import { caseDetailPath, ROUTES } from "@/app/routes";
import { PERMISSION_TARGETS } from "@/auth/permissions/permission-targets";
import { useCan } from "@/auth/permissions/use-permissions";
import { Skeleton } from "@/components/ui/skeleton";
import { usePendingApprovals } from "@/features/approvals/use-approvals";
import { EMPTY_FILTERS } from "@/features/my-applications/filters-url";
import { MyApplicationTable } from "@/features/my-applications/my-application-table";
import { useMyApplications } from "@/features/my-applications/use-my-applications";

import { summarizeMyCases } from "./use-home-summary";

const RECENT_COUNT = 5;

/**
 * 大項目1「ホーム / ダッシュボード」。
 *
 * 全員がここに着地し、権限に応じてカードを合成する（issue #29 の確定仕様）。
 * 件数は取得済みの一覧・タスクから集計してリクエストを増やさない。
 */
export function HomePage() {
  const { t } = useTranslation(["common", "domain"]);
  const canSeeApprovals = useCan(PERMISSION_TARGETS.taskList, "GET");
  const navigate = useNavigate();
  const backTo = backTargetFromLocation(useLocation());

  const cases = useMyApplications({
    filters: EMPTY_FILTERS,
    page: 1,
    perPage: 50,
  });
  const approvals = usePendingApprovals();

  const summary = summarizeMyCases(cases.data?.items ?? []);
  const pendingCount = approvals.data?.length ?? 0;
  const isLoading = cases.isPending || approvals.isPending;
  const needsAttention =
    pendingCount > 0 || summary.draft > 0 || summary.returned > 0;

  // 行クリックで詳細を開く（マイ案件と同じ操作にする）
  const openCase = useCallback(
    (caseId: number) => {
      const target = cases.data?.items.find((item) => item.caseId === caseId);
      if (target === undefined) return;
      void navigate(
        withBackTarget(
          caseDetailPath(target.caseId, target.processModelId),
          backTo,
        ),
      );
    },
    [backTo, cases.data, navigate],
  );

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">{t("common:home.title")}</h1>
      </header>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold">
            {t("common:home.actionNeeded")}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {canSeeApprovals ? (
              <SummaryCard
                to={ROUTES.approvalRequests}
                label={t("common:home.cards.pendingApprovals")}
                count={pendingCount}
                tone={pendingCount > 0 ? "attention" : "muted"}
              />
            ) : null}
            <SummaryCard
              to={ROUTES.myApplications}
              label={t("common:home.cards.draft")}
              count={summary.draft}
              tone="muted"
            />
            <SummaryCard
              to={ROUTES.myApplications}
              label={t("common:home.cards.returned")}
              count={summary.returned}
              tone={summary.returned > 0 ? "danger" : "muted"}
            />
            <SummaryCard
              to={ROUTES.myApplications}
              label={t("common:home.cards.completed")}
              count={summary.completed}
              tone="muted"
            />
          </div>

          {needsAttention ? null : (
            <p className="text-muted-foreground text-sm">
              {t("common:home.allClear")}
            </p>
          )}
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">{t("common:home.recent")}</h2>
        {cases.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <MyApplicationTable
            items={(cases.data?.items ?? []).slice(0, RECENT_COUNT)}
            onSelect={openCase}
          />
        )}
      </section>
    </section>
  );
}

function SummaryCard({
  to,
  label,
  count,
  tone,
}: {
  readonly to: string;
  readonly label: string;
  readonly count: number;
  readonly tone: "attention" | "danger" | "muted";
}): ReactNode {
  const countClass =
    tone === "danger"
      ? "text-destructive"
      : tone === "attention"
        ? "text-primary"
        : "text-foreground";

  return (
    <Link
      to={to}
      className="border-border hover:border-primary bg-card flex flex-col gap-1 rounded-lg border p-4"
    >
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className={`text-2xl font-semibold tabular-nums ${countClass}`}>
        {count}
      </span>
    </Link>
  );
}
